import { StatusLine } from '@/ui/status-line/StatusLine'
import { gql } from '@gql-generated/gql'
import type {
  ClickAndCollectStatus,
  ProductAvailabilityItem_AvailabilityFragment,
} from '@gql-generated/graphql'
import type { ProductAvailabilityType } from '../../types/productAvailabilities'
import type { ProductStatusLineType } from '../../utils/productAvailabilityItem'
import { getProductAvailabilityItem } from '../../utils/productAvailabilityItem'
import { ProductStatusLineTrans } from './ProductStatusLineTrans.client'

gql(/* GraphQL */ `
  fragment ProductStock_Availabilities on ProductStock {
    availability
    storeQuantity
    shippingDelay
  }

  fragment ProductAvailabilityItem_Availability on Product {
    stock {
      ...ProductStock_Availabilities
      maxQuantity
      onlineQuantity
      g9LowStockThreshold
    }
  }

  fragment FullProductAvailabilityItem_Availability on Product {
    canAddToCart
    clickAndCollectStatus
    stock {
      ...ProductStock_Availabilities
      maxQuantity
      onlineQuantity
      g9LowStockThreshold
      isPriceAvailable
      isSellable
    }
  }
`)

export type ProductStatusLineProps = {
  availability: ProductAvailabilityItem_AvailabilityFragment
  type: ProductAvailabilityType
  pointOfSaleName?: string
  cartQuantity?: number | null
  /**
   * If true, the product availability will be displayed in short mode
   * @default false
   */
  shortMode?: boolean
  notificationClassName?: string
  onlineQuantity?: number
  maxQuantity?: number
  customData?: ProductStatusLineType
  g9LowStockThreshold?: string | null
  canAddToCart: boolean
  clickAndCollectStatus?: ClickAndCollectStatus | null
}

export function ProductStatusLine({
  availability,
  type,
  pointOfSaleName,
  cartQuantity,
  shortMode = false,
  onlineQuantity,
  maxQuantity,
  customData,
  canAddToCart,
  clickAndCollectStatus,
}: ProductStatusLineProps) {
  const productAvailability =
    customData ||
    getProductAvailabilityItem({
      availability,
      type,
      pointOfSaleName,
      cartQuantity,
      shortMode,
      onlineQuantity,
      maxQuantity,
      canAddToCart,
      clickAndCollectStatus,
    })

  if (!productAvailability) {
    return null
  }

  const { i18nKey, values, statusDot, textTint } = productAvailability

  const trans = (
    <ProductStatusLineTrans
      i18nKey={i18nKey}
      values={values}
      textTint={textTint}
      shortMode={shortMode}
    />
  )

  return (
    <div data-testid={`product-availability/${type}`}>
      {statusDot?.variant ? <StatusLine variant={statusDot.variant}>{trans}</StatusLine> : trans}
    </div>
  )
}
